import $$ from 'dom7';

/*
 * Interaction layer for the auth screens (login / register / OTP / reset).
 *
 * Everything is delegated from `document`, so it survives page navigation and
 * works for the popups that live in index.html. It only adds behaviour on top
 * of the existing markup contract — the inputs the app already reads
 * (#username, #ac-otp, #otp, ...) keep their ids, names and values.
 */

var RESEND_SECONDS = 30;

/* Reverse geocoder for the "use my current location" button. Nominatim is
   free and key-less, which suits a low-volume "fill my address" action; swap
   this one constant for a paid geocoder if the volume ever justifies it. */
var GEOCODE_URL = 'https://nominatim.openstreetmap.org/reverse';

/*
 * Country availability by platform.
 *
 * The web/PWA build serves India, Bangladesh and Nepal. The packaged Cordova
 * app ships to India only, so every country control is pinned to IN there —
 * the options are removed rather than hidden, so nothing else in the app can
 * put a non-Indian code into a submitted form.
 */
var indiaOnly = null;

export function isIndiaOnly() {
  if (indiaOnly === null) {
    indiaOnly = !!(window.cordova || window.phonegap || window.PhoneGap);
  }
  return indiaOnly;
}

/* Safe to call repeatedly; a no-op off Cordova. */
export function applyCountryScope() {
  if (!isIndiaOnly()) return;
  $$('select.ypw-code__select, select[data-country-list]').each(function () {
    var select = this;
    for (var i = select.options.length - 1; i >= 0; i -= 1) {
      if (select.options[i].value !== 'IN') select.remove(i);
    }
    /* never disable it — a disabled control is dropped from serialize() */
    select.selectedIndex = 0;
  });
  $$('#ypw-country-sheet .ypw-country').each(function () {
    if (this.getAttribute('data-code') !== 'IN') $$(this).remove();
  });
  /* one choice is not a choice: stop the trigger behaving like a picker */
  $$('.ypw-code[data-country-for]').addClass('ypw-code--locked');
}

/*
 * Native event delegation.
 *
 * Deliberately not dom7's `.on(event, selector, fn)`: the app calls
 * `$$(document).off('click', '.logout')` on every pageInit, and dom7's `off`
 * with no listener argument drops *every* delegated listener for that event
 * on the element — selector ignored. Anything registered here would be torn
 * off on the first page load.
 */
function closestMatch(el, selector) {
  var node = el;
  while (node && node.nodeType === 1) {
    var matches =
      node.matches ||
      node.webkitMatchesSelector ||
      node.msMatchesSelector;
    if (matches && matches.call(node, selector)) return node;
    node = node.parentElement;
  }
  return null;
}

function delegate(types, selector, handler) {
  types.split(' ').forEach(function (type) {
    document.addEventListener(
      type,
      function (e) {
        var match = closestMatch(e.target, selector);
        if (match) handler.call(match, e);
      },
      false
    );
  });
}

/* -------------------------------------------------------------------------
   Field chrome
   ------------------------------------------------------------------------- */

function markFocus(el, focused) {
  var control = $$(el).parents('.ypw-control');
  if (control.length) control[focused ? 'addClass' : 'removeClass']('is-focused');
}

function clearError(el) {
  var field = $$(el).parents('.ypw-field');
  if (field.length) field.removeClass('is-invalid');
}

function showError(input, message) {
  var $field = $$(input).parents('.ypw-field');
  if (!$field.length) return;
  var $msg = $field.find('.ypw-error').eq(0);
  if ($msg.length) $msg.text(message);
  $field.addClass('is-invalid');
}

function messageFor(input) {
  var custom = input.getAttribute('data-error-message');
  var v = input.validity;
  /* setCustomValidity() wins: it is the only way to express a cross-field rule
     ("account numbers must match"), and its message is already specific. */
  if (v.customError) return input.validationMessage || custom || 'Please check this value';
  if (v.valueMissing) return custom || 'This field is required';
  if (v.typeMismatch && input.type === 'email') return 'Enter a valid email address';
  if (v.tooShort || v.patternMismatch || v.tooLong) {
    return custom || 'Please check this value';
  }
  return custom || 'Please check this value';
}

/*
 * Runs before the app's own click handler (which silently console.logs when
 * `checkValidity()` fails) so the user actually sees what is wrong.
 * It never blocks the request — a valid form is untouched.
 */
export function validateScope(scopeEl) {
  var fields = $$(scopeEl).find('input, select, textarea');
  var firstInvalid = null;
  for (var i = 0; i < fields.length; i += 1) {
    var input = fields[i];
    if (input.disabled || typeof input.checkValidity !== 'function') continue;
    if (input.checkValidity()) {
      clearError(input);
    } else {
      showError(input, messageFor(input));
      if (!firstInvalid) firstInvalid = input;
    }
  }
  if (firstInvalid) {
    /* Some controls carry the validation for a sibling the user can actually
       see (OTP boxes, the readonly date field) — focus that instead. */
    var $invalid = $$(firstInvalid);
    var $field = $invalid.parents('.ypw-field');
    var visible = firstInvalid;
    if ($invalid.hasClass('ypw-otp-value')) {
      visible = $field.find('.ypw-otp-box')[0];
    } else if ($invalid.hasClass('ypw-mirror')) {
      visible = $field.find('.ypw-control input')[0];
    }
    if (visible && visible.focus) visible.focus();
  }
  return !firstInvalid;
}

/* -------------------------------------------------------------------------
   OTP entry — six boxes writing into one hidden value input
   ------------------------------------------------------------------------- */

function otpBoxes(el) {
  var group = $$(el).parents('.ypw-field');
  return group.find('.ypw-otp-box');
}

function syncOtp(el) {
  var $field = $$(el).parents('.ypw-field');
  var value = '';
  $field.find('.ypw-otp-box').each(function () {
    value += (this.value || '').replace(/\D/g, '').charAt(0) || '';
  });
  $field.find('.ypw-otp-value').val(value);
  if (value.length === 6) $field.removeClass('is-invalid');
}

function paintFilled(boxes) {
  boxes.each(function () {
    $$(this)[this.value ? 'addClass' : 'removeClass']('is-filled');
  });
}

function fillFrom(startBox, digits) {
  var boxes = otpBoxes(startBox);
  var start = boxes.indexOf(startBox);
  if (start < 0) start = 0;
  for (var i = 0; i < digits.length && start + i < boxes.length; i += 1) {
    boxes[start + i].value = digits.charAt(i);
  }
  paintFilled(boxes);
  syncOtp(startBox);
  var next = Math.min(start + digits.length, boxes.length - 1);
  if (boxes[next] && boxes[next].focus) boxes[next].focus();
}

function resetOtp($field) {
  $field.find('.ypw-otp-box').val('').removeClass('is-filled');
  $field.find('.ypw-otp-value').val('');
  $field.removeClass('is-invalid');
}

/* -------------------------------------------------------------------------
   Country picker

   The visible trigger is a button; the real <select> stays in the DOM so the
   app's existing `.serialize()` and `$('#countryCode').val(...)` keep working.
   The sheet in index.html is the single source of flag/name/dial data.
   ------------------------------------------------------------------------- */

var activeCodeTrigger = null;

function countryRow(code) {
  return $$('#ypw-country-sheet .ypw-country').filter(function () {
    return this.getAttribute('data-code') === code;
  })[0];
}

function paintCodeTrigger($trigger) {
  var selectId = $trigger.attr('data-country-for');
  var select = document.getElementById(selectId);
  if (!select) return;
  /* Geolocation may hand us a country we do not serve; fall back rather than
     leaving the select on -1, which would drop it from the serialized form. */
  if (select.selectedIndex < 0) select.selectedIndex = 0;
  var row = countryRow(select.value);
  if (!row) {
    select.selectedIndex = 0;
    row = countryRow(select.value);
  }
  if (!row) return;
  $trigger.find('.ypw-code__flag').text(row.getAttribute('data-flag'));
  $trigger.find('.ypw-code__dial').text(row.getAttribute('data-dial'));
}

function paintAllCodeTriggers() {
  $$('.ypw-code[data-country-for]').each(function () {
    paintCodeTrigger($$(this));
  });
}

/* -------------------------------------------------------------------------
   Current location
   ------------------------------------------------------------------------- */

function setHint($field, message, state) {
  var $hint = $field.find('.ypw-hint');
  if (!$hint.length) return;
  $hint.removeClass('is-busy is-error');
  if (state) $hint.addClass(state);
  $hint.html(message);
}

function composeAddress(data) {
  if (!data) return '';
  var a = data.address || {};
  var street = [a.house_number, a.road].filter(Boolean).join(' ');
  var parts = [
    street,
    a.neighbourhood || a.suburb || a.village,
    a.city || a.town || a.county,
    a.state,
    a.postcode,
  ];
  var seen = {};
  var address = parts
    .filter(function (part) {
      if (!part || seen[part]) return false;
      seen[part] = true;
      return true;
    })
    .join(', ');
  return address || data.display_name || '';
}

function reverseGeocode(lat, lon, done, fail) {
  var url =
    GEOCODE_URL +
    '?format=jsonv2&addressdetails=1&zoom=18&lat=' +
    encodeURIComponent(lat) +
    '&lon=' +
    encodeURIComponent(lon);
  var xhr = new XMLHttpRequest();
  xhr.open('GET', url, true);
  xhr.timeout = 15000;
  xhr.onload = function () {
    if (xhr.status < 200 || xhr.status >= 300) {
      fail();
      return;
    }
    var data;
    try {
      data = JSON.parse(xhr.responseText);
    } catch (err) {
      fail();
      return;
    }
    var address = composeAddress(data);
    if (address) done(address);
    else fail();
  };
  xhr.onerror = function () {
    fail();
  };
  xhr.ontimeout = function () {
    fail();
  };
  xhr.send();
}

function geolocationMessage(err) {
  if (!err) return 'Could not find your location. Enter your address manually.';
  if (err.code === 1) {
    return 'Location permission denied. Allow it in settings or type your address.';
  }
  if (err.code === 3) return 'Locating timed out. Try again or type your address.';
  return 'Could not find your location. Enter your address manually.';
}

/* -------------------------------------------------------------------------
   Resend countdown
   ------------------------------------------------------------------------- */

function startCountdown(linkEl, seconds) {
  if (!linkEl) return;
  if (linkEl.ypwTimer) clearInterval(linkEl.ypwTimer);
  var label = linkEl.getAttribute('data-label');
  if (!label) {
    label = ($$(linkEl).text() || 'Resend').trim();
    linkEl.setAttribute('data-label', label);
  }
  var left = seconds;
  $$(linkEl).addClass('is-waiting').text('Resend in ' + left + 's');
  linkEl.ypwTimer = setInterval(function () {
    left -= 1;
    if (left <= 0) {
      clearInterval(linkEl.ypwTimer);
      linkEl.ypwTimer = null;
      $$(linkEl).removeClass('is-waiting').text(label);
    } else {
      $$(linkEl).text('Resend in ' + left + 's');
    }
  }, 1000);
}

/* -------------------------------------------------------------------------
   Wiring
   ------------------------------------------------------------------------- */

export default function initAuthUI(app) {
  /* prefer F7's detection; the window sniff above is the fallback */
  if (app && app.device && typeof app.device.cordova !== 'undefined') {
    indiaOnly = !!app.device.cordova;
  }
  applyCountryScope();

  var sheet = null;
  /* Built lazily so the app's global `closeByBackdropClick: false` — right for
     payment sheets, wrong for a picker — can be overridden just here. */
  function countrySheet() {
    if (!sheet) {
      sheet = app.sheet.create({
        el: '#ypw-country-sheet',
        closeByBackdropClick: true,
        on: {
          closed: function () {
            $$('.ypw-code').removeClass('is-open');
            activeCodeTrigger = null;
          },
        },
      });
    }
    return sheet;
  }

  /* Focus ring */
  delegate('focusin', '.ypw-control input, .ypw-control select', function () {
    markFocus(this, true);
  });
  delegate('focusout', '.ypw-control input, .ypw-control select', function () {
    markFocus(this, false);
  });

  /* Clear the error state as soon as the user starts fixing it */
  delegate('input change', '.ypw-field input, .ypw-field select', function () {
    clearError(this);
  });

  /* Digits-only fields (phone numbers) */
  delegate('input', '[data-digits]', function () {
    var cleaned = (this.value || '').replace(/\D/g, '');
    if (cleaned !== this.value) this.value = cleaned;
  });

  /* Password reveal */
  delegate('click', '.ypw-reveal', function (e) {
    e.preventDefault();
    var $btn = $$(this);
    var input = $btn.parents('.ypw-control').find('input')[0];
    if (!input) return;
    var show = input.type === 'password';
    input.type = show ? 'text' : 'password';
    $btn[show ? 'addClass' : 'removeClass']('is-on');
    $btn.find('i.icon').text(show ? 'eye_slash' : 'eye');
    $btn.attr('aria-label', show ? 'Hide password' : 'Show password');
  });

  /* OTP boxes */
  delegate('input', '.ypw-otp-box', function () {
    var digits = (this.value || '').replace(/\D/g, '');
    if (digits.length > 1) {
      this.value = '';
      fillFrom(this, digits);
      return;
    }
    this.value = digits;
    var boxes = otpBoxes(this);
    paintFilled(boxes);
    syncOtp(this);
    if (digits) {
      var next = boxes[boxes.indexOf(this) + 1];
      if (next && next.focus) next.focus();
    }
  });

  delegate('keydown', '.ypw-otp-box', function (e) {
    var boxes = otpBoxes(this);
    var index = boxes.indexOf(this);
    var codes = { 8: 'Backspace', 37: 'ArrowLeft', 39: 'ArrowRight' };
    var key = e.key || codes[e.keyCode];
    if (key === 'Backspace' && !this.value && index > 0) {
      e.preventDefault();
      var prev = boxes[index - 1];
      prev.value = '';
      $$(prev).removeClass('is-filled');
      syncOtp(this);
      prev.focus();
    } else if (key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      boxes[index - 1].focus();
    } else if (key === 'ArrowRight' && index < boxes.length - 1) {
      e.preventDefault();
      boxes[index + 1].focus();
    }
  });

  /* maxlength=1 would truncate a pasted code, so distribute it by hand */
  delegate('paste', '.ypw-otp-box', function (e) {
    var clipboard = e.clipboardData || window.clipboardData;
    if (!clipboard) return;
    var digits = (clipboard.getData('text') || '').replace(/\D/g, '');
    if (!digits) return;
    e.preventDefault();
    fillFrom(this, digits.substr(0, 6));
  });

  /* `focus` does not bubble, so delegation has to hang off `focusin` */
  delegate('focusin', '.ypw-otp-box', function () {
    if (this.select) this.select();
  });

  /* Country picker: open the shared sheet, remember who asked */
  delegate('click', '.ypw-code[data-country-for]', function (e) {
    e.preventDefault();
    if (isIndiaOnly()) return;
    activeCodeTrigger = $$(this);
    var current = document.getElementById(this.getAttribute('data-country-for'));
    $$('#ypw-country-sheet .ypw-country').each(function () {
      $$(this)[
        current && this.getAttribute('data-code') === current.value
          ? 'addClass'
          : 'removeClass'
      ]('is-selected');
    });
    $$(this).addClass('is-open');
    countrySheet().open();
  });

  delegate('click', '.ypw-country', function (e) {
    e.preventDefault();
    if (!activeCodeTrigger) return;
    var select = document.getElementById(activeCodeTrigger.attr('data-country-for'));
    if (select) {
      select.value = this.getAttribute('data-code');
      /* Let the rest of the app hear it, exactly as a native select would */
      $$(select).trigger('change');
    }
    paintCodeTrigger(activeCodeTrigger);
    countrySheet().close();
  });

  /* Keep the trigger honest when the value is set from elsewhere — the
     IP-geolocation lookup in routes.js does `.val(code).trigger('change')` */
  delegate('change', '.ypw-code__select', function () {
    var trigger = $$('.ypw-code[data-country-for="' + this.id + '"]');
    if (trigger.length) paintCodeTrigger(trigger);
  });

  /* Mirror a readonly picker field into the input that carries its validation */
  delegate('change', '#dob', function () {
    var mirror = $$('[data-mirror-of="dob"]');
    if (!mirror.length) return;
    mirror.val(this.value || '');
    if (this.value) clearError(mirror[0]);
  });

  /* Current location → full address */
  delegate('click', '.ypw-locate', function (e) {
    e.preventDefault();
    var trigger = this;
    var $field = $$(trigger).parents('.ypw-field');
    var input = $field.find('input[type="text"]').eq(0)[0];
    if (!input) return;

    if (!navigator.geolocation) {
      setHint($field, 'Location is not available on this device.', 'is-error');
      return;
    }

    var $buttons = $field.find('.ypw-locate');
    $buttons.addClass('is-busy');
    setHint($field, 'Finding your location&hellip;', 'is-busy');

    function finish(message, state) {
      $buttons.removeClass('is-busy');
      setHint($field, message, state);
    }

    navigator.geolocation.getCurrentPosition(
      function (position) {
        reverseGeocode(
          position.coords.latitude,
          position.coords.longitude,
          function (address) {
            input.value = address;
            $$(input).trigger('change');
            clearError(input);
            finish('Address filled in from your location — edit it if needed.');
          },
          function () {
            finish(
              'Found you, but could not read the address. Please type it in.',
              'is-error'
            );
          }
        );
      },
      function (err) {
        finish(geolocationMessage(err), 'is-error');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  });

  /* Copy-to-clipboard for anything carrying data-copy */
  delegate('click', '[data-copy]', function (e) {
    e.preventDefault();
    var button = this;
    var value = button.getAttribute('data-copy') || '';
    if (!value) return;
    function done() {
      $(button).addClass('is-copied');
      setTimeout(function () { $(button).removeClass('is-copied'); }, 1200);
      if (app && app.toast) {
        app.toast.show({ text: (button.getAttribute('data-copy-label') || 'Copied'), closeTimeout: 1400 });
      }
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(value).then(done, function () { legacyCopy(value); done(); });
    } else {
      legacyCopy(value);
      done();
    }
  });

  /* Inline validation for every auth call-to-action */
  delegate(
    'click',
    '.lgNum, .btnSignup, .fpBTN, .fpBTNotp, .acBTNotp, .transferMoney, .withdrawMoney',
    function () {
      /* auth screens wrap their fields in .ypw-shell; elsewhere fall back to
         the form, then the page. type="hidden" inputs are barred from
         constraint validation, so a page-wide scope stays safe. */
      var scope = $(this).parents('.ypw-shell, form, .page')[0];
      if (scope) validateScope(scope);
    }
  );

  /* Resend: hold the link for a moment so the code has time to arrive */
  delegate('click', '.ypw-resend', function () {
    startCountdown(this, RESEND_SECONDS);
  });

  /* Freshly rendered pages/popups may carry a pre-selected country */
  app.on('pageInit', function () {
    applyCountryScope();
    paintAllCodeTriggers();
  });

  /* Popup lifecycle: start each verification with clean boxes */
  app.on('popupOpen', function (popup) {
    var $el = popup && popup.$el ? popup.$el : $$(popup);
    if (!$el || !$el.length || !$el.hasClass('ypw-auth')) return;
    var $field = $el.find('.ypw-field').filter(function () {
      return $$(this).find('.ypw-otp-box').length > 0;
    });
    if (!$field.length) return;
    resetOtp($field);
    var resend = $el.find('.ypw-resend')[0];
    if (resend) startCountdown(resend, RESEND_SECONDS);
    setTimeout(function () {
      var first = $field.find('.ypw-otp-box')[0];
      if (first && first.focus) first.focus();
    }, 420);
  });

  app.on('popupClose', function (popup) {
    var $el = popup && popup.$el ? popup.$el : $$(popup);
    if (!$el || !$el.length || !$el.hasClass('ypw-auth')) return;
    $el.find('.ypw-field').each(function () {
      if ($$(this).find('.ypw-otp-box').length) resetOtp($$(this));
    });
    $el.find('.ypw-resend').each(function () {
      if (this.ypwTimer) {
        clearInterval(this.ypwTimer);
        this.ypwTimer = null;
        var label = this.getAttribute('data-label');
        $$(this).removeClass('is-waiting').text(label || 'Resend');
      }
    });
  });
}

/* -------------------------------------------------------------------------
   Helpers used by routes.js
   ------------------------------------------------------------------------- */

/*
 * Set a phone field's country from code. Use this instead of
 * `$('#countryCode').val(code).trigger('change')`: jQuery's synthetic change
 * never reaches native listeners, so the visible trigger would go stale.
 * It also refuses to leave the select on selectedIndex -1 when the detected
 * country is one we do not serve — which used to drop the field from
 * `.serialize()` entirely.
 */
export function setCountryCode(selectId, code) {
  var select = document.getElementById(selectId);
  if (!select) return;
  select.value = code || '';
  if (select.selectedIndex < 0) select.selectedIndex = 0;
  var $trigger = $$('.ypw-code[data-country-for="' + selectId + '"]');
  if ($trigger.length) paintCodeTrigger($trigger);
}

var MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function syncCalendarBar(calendar) {
  var $el = calendar.$el;
  if (!$el || !$el.length) return;
  $el.find('.ypw-cal-month').val(String(calendar.currentMonth));
  $el.find('.ypw-cal-year').val(String(calendar.currentYear));
}

/*
 * Date-of-birth picker: an F7 calendar in a sheet, with month and year jump
 * selects in place of F7 4.x's one-step-at-a-time arrows — nobody is clicking
 * back thirty years one month at a time.
 */
export function createDobCalendar(app, inputEl, maxAge) {
  var today = new Date();
  var thisYear = today.getFullYear();
  /* The server rejects signups outside 18-120 years old (KYC: 18-100), so the
     picker simply does not offer those dates. */
  var oldestAge = maxAge || 120;
  var oldest = new Date(today.getFullYear() - oldestAge, today.getMonth(), today.getDate());
  var youngest = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());

  return app.calendar.create({
    inputEl: inputEl,
    openIn: 'sheet',
    closeOnSelect: true,
    dateFormat: 'dd/mm/yyyy',
    maxDate: youngest,
    minDate: oldest,
    /* Every jump finishes at once. F7 4.x only tidies up after a month
       change on the wrapper's transitionend, and a zero-length jump (what the
       selects below need) never fires one: the old months stayed in the
       slider, one more per jump, drawn over each other and swallowing taps.
       With animate:false F7 completes each change itself. */
    animate: false,
    cssClass: 'ypw-auth ypw-calendar',
    renderToolbar: function () {
      var months = MONTH_NAMES.map(function (name, i) {
        return '<option value="' + i + '">' + name + '</option>';
      }).join('');
      var years = '';
      for (var y = thisYear - 18; y >= thisYear - oldestAge; y -= 1) {
        years += '<option value="' + y + '">' + y + '</option>';
      }
      return (
        '<div class="ypw-cal-bar">' +
        '<select class="ypw-cal-select ypw-cal-month" aria-label="Month">' + months + '</select>' +
        '<select class="ypw-cal-select ypw-cal-year" aria-label="Year">' + years + '</select>' +
        '<button type="button" class="ypw-cal-done sheet-close no-ripple">Done</button>' +
        '</div>'
      );
    },
    on: {
      open: function (calendar) {
        /* Open near a plausible birth year rather than this month */
        if (!calendar.value || !calendar.value.length) {
          calendar.setYearMonth(thisYear - 25, 0);
        }
        syncCalendarBar(calendar);

        /* A month outside the bounds (after the youngest date, before the
           oldest) lands on the nearest allowed one. F7 would refuse the jump
           and leave the selects naming a month the grid is not showing. */
        function jump(year, month) {
          if (year === youngest.getFullYear() && month > youngest.getMonth()) month = youngest.getMonth();
          if (year === oldest.getFullYear() && month < oldest.getMonth()) month = oldest.getMonth();
          if ((year === calendar.currentYear && month === calendar.currentMonth) ||
              calendar.setYearMonth(year, month) === false) {
            syncCalendarBar(calendar);
          }
        }
        /* The sheet is re-rendered on every open, so these bind fresh */
        calendar.$el.find('.ypw-cal-month').on('change', function () {
          jump(calendar.currentYear, parseInt(this.value, 10));
        });
        calendar.$el.find('.ypw-cal-year').on('change', function () {
          jump(parseInt(this.value, 10), calendar.currentMonth);
        });
      },
      monthYearChangeStart: function (calendar) {
        syncCalendarBar(calendar);
      },
    },
  });
}


/* -------------------------------------------------------------------------
   Clipboard fallback for WebViews without navigator.clipboard
   ------------------------------------------------------------------------- */

function legacyCopy(value) {
  var temp = document.createElement('textarea');
  temp.value = value;
  temp.setAttribute('readonly', '');
  temp.style.position = 'absolute';
  temp.style.left = '-9999px';
  document.body.appendChild(temp);
  temp.select();
  try { document.execCommand('copy'); } catch (err) { /* nothing else to try */ }
  document.body.removeChild(temp);
}

/* -------------------------------------------------------------------------
   "Pay to" bank / UPI details

   One renderer for the fee-payment and investment-payment screens, which
   previously each carried three near-identical branches of concatenated
   markup. Returns false when neither a bank account nor a UPI id is
   configured, so the caller can also hide its payment form.
   ------------------------------------------------------------------------- */

function payRow(label, value, copyLabel) {
  var row = document.createElement('div');
  row.className = 'ypw-payto__row';

  var key = document.createElement('span');
  key.className = 'ypw-payto__label';
  key.textContent = label;
  row.appendChild(key);

  var right = document.createElement('span');
  right.className = 'ypw-payto__value';

  var text = document.createElement('b');
  text.textContent = value;
  right.appendChild(text);

  var copy = document.createElement('button');
  copy.type = 'button';
  copy.className = 'ypw-copy no-ripple';
  copy.setAttribute('data-copy', value);
  copy.setAttribute('data-copy-label', copyLabel || (label + ' copied'));
  copy.setAttribute('aria-label', 'Copy ' + label.toLowerCase());
  copy.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
    '<rect x="9" y="9" width="11" height="11" rx="2.5"/>' +
    '<path d="M15 6.5A2.5 2.5 0 0 0 12.5 4h-6A2.5 2.5 0 0 0 4 6.5v6A2.5 2.5 0 0 0 6.5 15"/></svg>';
  right.appendChild(copy);

  row.appendChild(right);
  return row;
}

function hasValue(v) {
  if (v === undefined || v === null) return false;
  var s = String(v).trim();
  return s !== '' && s !== 'undefined' && s !== 'null' && s !== '0';
}

export function renderBankDetails(target, data, base) {
  var $box = $$(target);
  if (!$box.length) return false;
  $box.empty();

  var hasBank = String(data.isBank) === '1';
  var hasUPI = String(data.isUPI) === '1';

  if (!hasBank && !hasUPI) {
    $box.append('<div class="ypw-nomethod">' +
      '<span class="ypw-nomethod__icon">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5.5" width="18" height="13" rx="2.5"/><path d="M3 10h18"/><path d="m4 20 16-16"/></svg>' +
      '</span>' +
      '<span class="ypw-nomethod__body">' +
      '<span class="ypw-nomethod__title">No payment methods available</span>' +
      '<span class="ypw-nomethod__text">We can\u2019t accept payments right now. Please try again shortly.</span>' +
      '<a href="#" class="ypw-nomethod__link popup-open" data-popup="#help-popup">Contact support</a>' +
      '</span></div>');
    return false;
  }

  var wrap = document.createElement('div');
  wrap.className = 'ypw-payto';

  if (hasBank) {
    var rows = document.createElement('div');
    rows.className = 'ypw-payto__rows';
    if (hasValue(data.beneficiary)) rows.appendChild(payRow('Beneficiary', data.beneficiary));
    if (hasValue(data.acc_name)) rows.appendChild(payRow('Bank', data.acc_name));
    if (hasValue(data.acc_num)) rows.appendChild(payRow('Account number', data.acc_num));
    if (hasValue(data.acc_ifsc)) rows.appendChild(payRow('IFSC / SWIFT', data.acc_ifsc));
    wrap.appendChild(rows);
  }

  if (hasBank && hasUPI) {
    var divider = document.createElement('div');
    divider.className = 'ypw-payto__or';
    divider.innerHTML = '<span>or pay by UPI</span>';
    wrap.appendChild(divider);
  }

  if (hasUPI) {
    var upi = document.createElement('div');
    upi.className = 'ypw-payto__upi';
    if (hasValue(data.upi_qrIMG)) {
      var qr = document.createElement('img');
      qr.className = 'ypw-payto__qr';
      qr.setAttribute('alt', 'UPI QR code');
      qr.setAttribute('src', (base || '') + data.upi_qrIMG);
      upi.appendChild(qr);
    }
    if (hasValue(data.upi_id)) {
      var upiRows = document.createElement('div');
      upiRows.className = 'ypw-payto__rows';
      upiRows.appendChild(payRow('UPI ID', data.upi_id));
      upi.appendChild(upiRows);
    }
    wrap.appendChild(upi);
  }

  $box.append(wrap);
  return true;
}

/* -------------------------------------------------------------------------
   Deposit account (subscribeInfo -> accounts[])

   Each entry carries method:"bank" or method:"upi" and every field is present
   even when empty, so it is safe to index blindly. A UPI entry rendered as a
   bank card is four blank rows, which is why this branches rather than
   assuming the bank shape.
   ------------------------------------------------------------------------- */

export function renderDepositAccount(target, account) {
  var $box = $$(target);
  if (!$box.length) return false;
  $box.empty();
  if (!account) return false;

  var wrap = document.createElement('div');
  wrap.className = 'ypw-payto';

  if (String(account.method) === 'upi') {
    var upi = document.createElement('div');
    upi.className = 'ypw-payto__upi';

    if (hasValue(account.upiQR)) {
      var qr = document.createElement('img');
      qr.className = 'ypw-payto__qr';
      qr.setAttribute('alt', 'UPI QR code');
      /* upiQR is an absolute URL from the server; no baseURL prefixing. */
      qr.setAttribute('src', account.upiQR);
      upi.appendChild(qr);
    }

    var upiRows = document.createElement('div');
    upiRows.className = 'ypw-payto__rows';
    var payee = account.upiName || account.beneficiaryName || account.label;
    if (hasValue(payee)) upiRows.appendChild(payRow('Pay to', payee));
    if (hasValue(account.upiID)) upiRows.appendChild(payRow('UPI ID', account.upiID));
    upi.appendChild(upiRows);

    wrap.appendChild(upi);
  } else {
    var rows = document.createElement('div');
    rows.className = 'ypw-payto__rows';
    if (hasValue(account.beneficiaryName)) rows.appendChild(payRow('Beneficiary', account.beneficiaryName));
    if (hasValue(account.bankName)) rows.appendChild(payRow('Bank', account.bankName));
    if (hasValue(account.bankAC)) rows.appendChild(payRow('Account number', account.bankAC));
    if (hasValue(account.bankIFSC)) rows.appendChild(payRow('IFSC / SWIFT', account.bankIFSC));
    wrap.appendChild(rows);
  }

  $box.append(wrap);
  return true;
}
